// Thin API client — every call attaches `Authorization: Bearer <idToken>`,
// exactly as src/worker.js and public/index.html expect. This module is the
// ONLY place that talks to the network for auth/session purposes; page-level
// domain data still comes from the store (INV-1).
import { API_BASE } from './config';
import { withViewAs } from './viewAs';
import type { Role } from './nav';
import { fetchRead } from './apiClient';

export interface MeResponse {
  email: string;
  signedIn: boolean;
  mapped: boolean;
  role: Role | null;
  displayName: string | null;
  admin: boolean;
  /** OWNER only. Unaffected by the lens, so the pulldown never hides itself. */
  canViewAs: boolean;
  /** True while a lens is active. The server sends a BOOLEAN here (worker.js
   * `/api/me` passes `ctx.viewingAs` straight through, and access.js sets it
   * with `Boolean(valid)`); this was typed `string | null`, which was simply
   * wrong and would have made any use of it misread. The CODE being viewed
   * lives client-side, in lib/viewAs.ts. */
  viewingAs: boolean;
  locations: string[];
  /** Present once, right after a Google sign-in. The client stores this in
   * place of the hour-long Google ID token so the person stays in 30 days. */
  sessionToken?: string;
}

/**
 * A failed API call that still knows its HTTP status.
 *
 * The status is the whole point: 401 means "this token is no longer good,
 * sign in again", and everything else means "something is broken, say so".
 * Collapsing both into one Error string is what left an expired token
 * rendering a dead-end fault with no way back to the sign-in button — a
 * Google ID token lasts about an hour, so that is every user, every day.
 */
export class HttpError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

/**
 * GET /api/me with the given bearer token. Throws on non-2xx or network failure.
 *
 * The view-as lens applies here as well, and that is the whole reason the lens
 * is convincing: `locations` from this endpoint is what the clinic strip
 * (ClinicTabs) and the Money forms' clinic pickers are built from, so without
 * `?as=` the owner would pick a clinic and still see all four tabs. `canViewAs`
 * is `isOwner` regardless of the lens (src/access.js), so narrowing this call
 * can never hide the control that undoes it.
 */
export async function fetchMe(idToken: string): Promise<MeResponse> {
  // The first call of every visit — retried once if the connection hangs (N-091).
  const res = await fetchRead(`${API_BASE}${withViewAs('/api/me')}`, {
    headers: { authorization: `Bearer ${idToken}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new HttpError((body as { error?: string }).error || `HTTP ${res.status}`, res.status);
  }
  return body as MeResponse;
}
